import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Edit,
  Play,
  Pause,
  Copy,
  Share,
  Download,
  Settings,
  MoreHorizontal,
  Mail,
  Eye,
  MousePointer,
  MessageSquare,
  Users,
  Target,
  TrendingUp,
  BarChart3,
  Activity,
  Calendar,
  Clock,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Zap,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card';
import { cn } from '../../../utils/cn';

// Import analytics components
import CampaignMetrics from '../../email/analytics/CampaignMetrics';
import RecipientJourney from '../../email/analytics/RecipientJourney';
import EngagementHeatmap from '../../email/analytics/EngagementHeatmap';

interface CampaignDetailPageProps {}

interface CampaignDetail {
  id: string;
  name: string;
  description: string;
  type: 'email' | 'linkedin' | 'cold_call' | 'sms' | 'multi_channel';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  objective: string;
  tags: string[];
  budget: number;
  spent: number;
  currency: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  metrics: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    totalReplied: number;
    openRate: number;
    clickRate: number;
    replyRate: number;
    conversionRate: number;
    revenue: number;
    roi: number;
  };
  performance: {
    daily: Array<{
      date: string;
      sent: number;
      opened: number;
      clicked: number;
      replied: number;
    }>;
  };
  traceId?: string;
}

const mockCampaign: CampaignDetail = {
  id: '1',
  name: 'Q4 Product Launch Campaign',
  description: 'Comprehensive outreach campaign for our new product launch targeting enterprise customers in the technology sector.',
  type: 'multi_channel',
  status: 'active',
  priority: 'high',
  objective: 'Product Launch',
  tags: ['product-launch', 'enterprise', 'technology', 'q4'],
  budget: 50000,
  spent: 32500,
  currency: 'USD',
  startDate: '2024-01-01',
  endDate: '2024-03-31',
  createdAt: '2023-12-15',
  updatedAt: '2024-01-20',
  metrics: {
    totalSent: 12450,
    totalOpened: 8515,
    totalClicked: 2934,
    totalReplied: 1247,
    openRate: 68.4,
    clickRate: 23.6,
    replyRate: 10.0,
    conversionRate: 3.2,
    revenue: 485000,
    roi: 1492
  },
  performance: {
    daily: [
      { date: '2024-01-15', sent: 450, opened: 308, clicked: 89, replied: 23 },
      { date: '2024-01-16', sent: 520, opened: 356, clicked: 102, replied: 31 },
      { date: '2024-01-17', sent: 380, opened: 260, clicked: 75, replied: 19 },
    ]
  }
};

const statusColors = {
  draft: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  paused: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  archived: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
};

const priorityColors = {
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  medium: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  urgent: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

export const CampaignDetailPage: React.FC<CampaignDetailPageProps> = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaign, setCampaign] = useState<CampaignDetail>(mockCampaign);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'journey' | 'heatmap' | 'settings'>('overview');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadCampaign(id);
    }
  }, [id]);

  const loadCampaign = async (campaignId: string) => {
    setIsLoading(true);
    try {
      // Load campaign data from API
      // const response = await campaignService.getCampaign(campaignId);
      // setCampaign(response.data);
    } catch (error) {
      console.error('Failed to load campaign:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      // Update campaign status
      setCampaign(prev => ({ ...prev, status: newStatus as any }));
    } catch (error) {
      console.error('Failed to update campaign status:', error);
    }
  };

  const getStatusAction = () => {
    switch (campaign.status) {
      case 'draft':
        return { label: 'Launch', icon: Play, action: () => handleStatusChange('active') };
      case 'active':
        return { label: 'Pause', icon: Pause, action: () => handleStatusChange('paused') };
      case 'paused':
        return { label: 'Resume', icon: Play, action: () => handleStatusChange('active') };
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: campaign.currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {campaign.metrics.totalSent.toLocaleString()}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Sent</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              {campaign.metrics.openRate}%
            </span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {campaign.metrics.totalOpened.toLocaleString()}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Opens</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <MousePointer className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
              {campaign.metrics.clickRate}%
            </span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {campaign.metrics.totalClicked.toLocaleString()}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Clicks</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
              {campaign.metrics.replyRate}%
            </span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {campaign.metrics.totalReplied.toLocaleString()}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Replies</p>
          </div>
        </Card>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Revenue & ROI
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Total Revenue</span>
              <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(campaign.metrics.revenue)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Budget Spent</span>
              <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {formatCurrency(campaign.spent)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">ROI</span>
              <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {campaign.metrics.roi}%
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Campaign Progress
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-600 dark:text-neutral-400">Budget Usage</span>
                <span className="text-neutral-900 dark:text-neutral-100">
                  {((campaign.spent / campaign.budget) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${(campaign.spent / campaign.budget) * 100}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-600 dark:text-neutral-400">Time Progress</span>
                <span className="text-neutral-900 dark:text-neutral-100">65%</span>
              </div>
              <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '65%' }} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Recent Activity
        </h3>
        <div className="space-y-4">
          {[
            { time: '2 hours ago', event: 'Email sequence step 3 sent to 450 recipients', type: 'info' },
            { time: '4 hours ago', event: 'High engagement detected from TechCorp Solutions', type: 'success' },
            { time: '6 hours ago', event: 'A/B test variant B showing 15% better performance', type: 'success' },
            { time: '1 day ago', event: 'Campaign budget 65% utilized', type: 'warning' },
            { time: '2 days ago', event: 'New qualified lead: Sarah Johnson', type: 'success' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className={cn(
                "w-2 h-2 rounded-full",
                activity.type === 'success' ? 'bg-green-500' :
                activity.type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
              )} />
              <div className="flex-1">
                <p className="text-sm text-neutral-900 dark:text-neutral-100">{activity.event}</p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {campaign.traceId && (
        <div className="flex items-center space-x-2">
          <span className="text-xs text-neutral-500">Trace ID:</span>
          <span className="font-mono text-xs text-blue-500">{campaign.traceId}</span>
          <Button size="xs" variant="outline" onClick={() => navigate(`/costs?tab=api-logs&traceId=${campaign.traceId}`)}>
            View API Log
          </Button>
        </div>
      )}
    </div>
  );

  const renderPerformance = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Detailed Performance Metrics
          </h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        <CampaignMetrics />
      </Card>
    </div>
  );

  const renderJourney = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Recipient Journey Analysis
          </h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        <RecipientJourney />
      </Card>
    </div>
  );

  const renderHeatmap = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Engagement Heatmap
          </h3>
          <div className="flex items-center space-x-2">
            <select className="px-3 py-1 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        <EngagementHeatmap campaignId={campaign.id} />
      </Card>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Campaign Settings
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Campaign Name
              </label>
              <input
                type="text"
                value={campaign.name}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Priority
              </label>
              <select
                value={campaign.priority}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Description
            </label>
            <textarea
              value={campaign.description}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
              readOnly
            />
          </div>
        </div>
      </Card>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Loading campaign...
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Please wait while we fetch your campaign data
          </p>
        </div>
      </div>
    );
  }

  const statusAction = getStatusAction();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/campaigns')}
                className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Campaigns
              </Button>
              <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700" />
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {campaign.name}
                  </h1>
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium",
                    statusColors[campaign.status]
                  )}>
                    {campaign.status}
                  </span>
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium",
                    priorityColors[campaign.priority]
                  )}>
                    {campaign.priority}
                  </span>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                  {campaign.objective} • Created {formatDate(campaign.createdAt)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {statusAction && (
                <Button
                  variant="outline"
                  onClick={statusAction.action}
                  className="flex items-center"
                >
                  <statusAction.icon className="w-4 h-4 mr-2" />
                  {statusAction.label}
                </Button>
              )}
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline">
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </Button>
              <Button variant="outline">
                <Share className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg mb-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'performance', label: 'Performance', icon: TrendingUp },
            { id: 'journey', label: 'Journey', icon: Users },
            { id: 'heatmap', label: 'Heatmap', icon: Activity },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  activeTab === tab.id
                    ? "bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm"
                    : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'performance' && renderPerformance()}
            {activeTab === 'journey' && renderJourney()}
            {activeTab === 'heatmap' && renderHeatmap()}
            {activeTab === 'settings' && renderSettings()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}; 